import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BookOpen,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Edit3,
  Save,
  Trash2,
  X,
  XCircle
} from 'lucide-react';

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';

import { db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/auth/AuthProvider';
import TeacherShell from './TeacherShell';

const EMPTY_FORM = {
  assignmentId: '',
  title: '',
  instructions: '',
  deadline: ''
};

function toMillis(value) {
  if (!value) return 0;

  if (typeof value?.toMillis === 'function') {
    return value.toMillis();
  }

  const parsed = new Date(value).getTime();

  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDeadline(value) {
  if (!value) return 'Date non définie';

  const date = new Date(`${value}T12:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-TN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(date);
}

function homeworkStatus(status) {
  if (status === 'approved') {
    return {
      label: 'Validé',
      icon: CheckCircle2,
      background: '#dcfce7'
    };
  }

  if (status === 'rejected') {
    return {
      label: 'Refusé',
      icon: XCircle,
      background: '#fee2e2'
    };
  }

  return {
    label: 'En attente',
    icon: Clock3,
    background: '#fef3c7'
  };
}

export default function TeacherHomeworkPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [assignments, setAssignments] = useState([]);
  const [homework, setHomework] = useState([]);

  const [loadingAssignments, setLoadingAssignments] =
    useState(true);

  const [loadingHomework, setLoadingHomework] =
    useState(true);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const teacherName =
    profile?.fullname ||
    profile?.fullName ||
    profile?.displayName ||
    'Enseignant';

  const selectedAssignment = useMemo(
    () =>
      assignments.find(
        (assignment) =>
          assignment.id === form.assignmentId
      ) || null,
    [assignments, form.assignmentId]
  );

  // ==================================================
  // AFFECTATIONS
  // ==================================================

  useEffect(() => {
    if (!user?.uid) return;

    let cancelled = false;

    async function loadAssignments() {
      setLoadingAssignments(true);

      try {
        const assignmentsQuery = query(
          collection(db, 'teacherAssignments'),
          where('teacherId', '==', user.uid)
        );

        const snapshot = await getDocs(assignmentsQuery);

        const activeAssignments = snapshot.docs
          .map((item) => ({
            id: item.id,
            ...item.data()
          }))
          .filter(
            (assignment) =>
              assignment.status === 'active'
          );

        const enriched = await Promise.all(
          activeAssignments.map(
            async (assignment) => {
              const [
                classSnapshot,
                subjectSnapshot
              ] = await Promise.all([
                getDoc(
                  doc(
                    db,
                    'classes',
                    assignment.classId
                  )
                ),
                getDoc(
                  doc(
                    db,
                    'subjects',
                    assignment.subjectId
                  )
                )
              ]);

              const classData =
                classSnapshot.exists()
                  ? classSnapshot.data()
                  : {};

              const subjectData =
                subjectSnapshot.exists()
                  ? subjectSnapshot.data()
                  : {};

              return {
                ...assignment,

                className:
                  classData.name ||
                  classData.className ||
                  assignment.className ||
                  assignment.classId,

                subjectName:
                  subjectData.name ||
                  subjectData.subjectName ||
                  assignment.subjectName ||
                  assignment.subjectId
              };
            }
          )
        );

        if (cancelled) return;

        setAssignments(enriched);

        setForm((current) => ({
          ...current,
          assignmentId:
            current.assignmentId ||
            enriched[0]?.id ||
            ''
        }));
      } catch (loadError) {
        console.error(
          'Erreur chargement affectations :',
          loadError
        );

        if (!cancelled) {
          setError(
            'Impossible de charger vos classes et matières.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingAssignments(false);
        }
      }
    }

    loadAssignments();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // ==================================================
  // DEVOIRS DE L'ENSEIGNANT
  // ==================================================

  useEffect(() => {
    if (!user?.uid) return;

    let cancelled = false;

    async function loadHomework() {
      setLoadingHomework(true);

      try {
        const homeworkQuery = query(
          collection(db, 'homework'),
          where('teacherId', '==', user.uid)
        );

        const snapshot = await getDocs(homeworkQuery);

        if (cancelled) return;

        const rows = snapshot.docs
          .map((item) => ({
            id: item.id,
            ...item.data()
          }))
          .sort(
            (a, b) =>
              toMillis(
                b.submittedAt ||
                  b.updatedAt ||
                  b.createdAt
              ) -
              toMillis(
                a.submittedAt ||
                  a.updatedAt ||
                  a.createdAt
              )
          );

        setHomework(rows);
      } catch (loadError) {
        console.error(
          'Erreur chargement devoirs :',
          loadError
        );

        if (!cancelled) {
          setError(
            'Impossible de charger vos devoirs.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingHomework(false);
        }
      }
    }

    loadHomework();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  function updateForm(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value
    }));

    setNotice('');
  }

  function resetForm() {
    setEditingId(null);

    setForm({
      ...EMPTY_FORM,
      assignmentId:
        assignments[0]?.id || ''
    });

    setError('');
    setNotice('');
  }

  // ==================================================
  // SOUMISSION
  // ==================================================

  async function submitHomework(event) {
    event.preventDefault();

    setError('');
    setNotice('');

    if (!selectedAssignment) {
      setError(
        'Sélectionnez une classe et une matière.'
      );
      return;
    }

    if (!form.title.trim()) {
      setError('Indiquez un titre.');
      return;
    }

    if (!form.instructions.trim()) {
      setError(
        'Indiquez la consigne du travail.'
      );
      return;
    }

    if (!form.deadline) {
      setError(
        'Indiquez une date limite.'
      );
      return;
    }

    setSaving(true);

    try {
      const payload = {
        teacherId: user.uid,
        teacherName,

        classId:
          selectedAssignment.classId,

        className:
          selectedAssignment.className,

        subjectId:
          selectedAssignment.subjectId,

        subjectName:
          selectedAssignment.subjectName,

        // Utilisé aussi par ParentPortal.
        subject:
          selectedAssignment.subjectName,

        title:
          form.title.trim(),

        instructions:
          form.instructions.trim(),

        deadline:
          form.deadline,

        schoolYear:
          selectedAssignment.schoolYear ||
          '2026-2027',

        // IMPORTANT :
        // jamais visible parent avant validation.
        status: 'pending',

        submittedAt:
          serverTimestamp(),

        updatedAt:
          serverTimestamp()
      };

      if (editingId) {
        await updateDoc(
          doc(db, 'homework', editingId),
          payload
        );

        setHomework((current) =>
          current.map((item) =>
            item.id === editingId
              ? {
                  ...item,
                  ...payload,
                  status: 'pending',
                  submittedAt: new Date(),
                  updatedAt: new Date()
                }
              : item
          )
        );

        setNotice(
          'Le travail a été renvoyé à l’administration pour validation.'
        );
      } else {
        const created = await addDoc(
          collection(db, 'homework'),
          {
            ...payload,
            createdAt:
              serverTimestamp()
          }
        );

        setHomework((current) => [
          {
            id: created.id,
            ...payload,
            status: 'pending',
            createdAt: new Date(),
            submittedAt: new Date(),
            updatedAt: new Date()
          },
          ...current
        ]);

        setNotice(
          'Le travail a été envoyé à l’administration pour validation.'
        );
      }

      setEditingId(null);

      setForm({
        assignmentId:
          selectedAssignment.id,
        title: '',
        instructions: '',
        deadline: ''
      });
    } catch (saveError) {
      console.error(
        'Erreur soumission travail :',
        saveError
      );

      setError(
        saveError?.message ||
          'Impossible de soumettre le travail.'
      );
    } finally {
      setSaving(false);
    }
  }

  // ==================================================
  // MODIFIER
  // ==================================================

  function editHomework(item) {
    if (item.status === 'approved') {
      setError(
        'Un travail déjà validé par l’administration ne peut plus être modifié.'
      );
      return;
    }

    const assignment = assignments.find(
      (candidate) =>
        candidate.classId === item.classId &&
        candidate.subjectId === item.subjectId
    );

    if (!assignment) {
      setError(
        'Cette affectation n’est plus active.'
      );
      return;
    }

    setEditingId(item.id);

    setForm({
      assignmentId:
        assignment.id,

      title:
        item.title || '',

      instructions:
        item.instructions ||
        item.description ||
        '',

      deadline:
        item.deadline || ''
    });

    setError('');
    setNotice('');

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  // ==================================================
  // SUPPRIMER
  // ==================================================

  async function removeHomework(item) {
    if (item.status === 'approved') {
      setError(
        'Un travail validé ne peut pas être supprimé par l’enseignant.'
      );
      return;
    }

    const confirmed = window.confirm(
      `Supprimer le travail « ${
        item.title || 'Sans titre'
      } » ?`
    );

    if (!confirmed) return;

    setDeletingId(item.id);
    setError('');
    setNotice('');

    try {
      await deleteDoc(
        doc(
          db,
          'homework',
          item.id
        )
      );

      setHomework((current) =>
        current.filter(
          (row) =>
            row.id !== item.id
        )
      );

      if (editingId === item.id) {
        resetForm();
      }

      setNotice(
        'Le travail a été supprimé.'
      );
    } catch (deleteError) {
      console.error(
        'Erreur suppression travail :',
        deleteError
      );

      setError(
        'Impossible de supprimer le travail.'
      );
    } finally {
      setDeletingId('');
    }
  }

  return (
    <TeacherShell activePage="homework">
        <section className="parent-section-title teacher-content-heading-v1">
          <div>
            <p className="eyebrow">
              PÉDAGOGIE
            </p>

            <h1>
              Travail à faire
            </h1>

            <p className="muted">
              Préparez les travaux de vos classes.
              Ils seront transmis aux parents
              uniquement après validation par
              l’administration.
            </p>
          </div>
        </section>

        {/* FORMULAIRE */}

        <section
          className="parent-card-v0"
          style={{
            marginBottom: '24px'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '22px'
            }}
          >
            <BookOpen size={28} />

            <div>
              <h2
                style={{
                  margin: 0
                }}
              >
                {editingId
                  ? 'Modifier le travail'
                  : 'Nouveau travail'}
              </h2>

              <p
                className="muted"
                style={{
                  margin: '4px 0 0'
                }}
              >
                Après soumission, le travail
                sera placé en attente de
                validation.
              </p>
            </div>
          </div>

          {loadingAssignments ? (
            <p className="muted">
              Chargement de vos affectations…
            </p>
          ) : assignments.length === 0 ? (
            <div className="alert alert-error">
              Aucune classe ou matière ne vous
              est attribuée.
            </div>
          ) : (
            <form
              onSubmit={submitHomework}
              style={{
                display: 'grid',
                gap: '18px'
              }}
            >
              <label>
                Classe et matière

                <select
                  value={form.assignmentId}
                  onChange={(event) =>
                    updateForm(
                      'assignmentId',
                      event.target.value
                    )
                  }
                  disabled={Boolean(editingId)}
                  required
                >
                  {assignments.map(
                    (assignment) => (
                      <option
                        key={assignment.id}
                        value={assignment.id}
                      >
                        {assignment.className}
                        {' — '}
                        {assignment.subjectName}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                Titre

                <input
                  type="text"
                  value={form.title}
                  onChange={(event) =>
                    updateForm(
                      'title',
                      event.target.value
                    )
                  }
                  placeholder="Exemple : Exercices de conjugaison"
                  maxLength={150}
                  required
                />
              </label>

              <label>
                Consigne

                <textarea
                  value={
                    form.instructions
                  }
                  onChange={(event) =>
                    updateForm(
                      'instructions',
                      event.target.value
                    )
                  }
                  placeholder="Exemple : Faire les exercices 1 à 5 page 36."
                  rows={5}
                  required
                />
              </label>

              <label>
                Date limite

                <input
                  type="date"
                  value={form.deadline}
                  onChange={(event) =>
                    updateForm(
                      'deadline',
                      event.target.value
                    )
                  }
                  required
                />
              </label>

              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              {notice && (
                <div
                  style={{
                    padding: '14px',
                    borderRadius: '12px',
                    background: '#dcfce7'
                  }}
                >
                  {notice}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap'
                }}
              >
                <button
                  type="submit"
                  className="button button-primary"
                  disabled={saving}
                >
                  <Save size={18} />

                  {saving
                    ? 'Envoi…'
                    : editingId
                      ? 'Renvoyer à validation'
                      : 'Soumettre à validation'}
                </button>

                {editingId && (
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={resetForm}
                  >
                    <X size={18} />
                    Annuler
                  </button>
                )}
              </div>
            </form>
          )}
        </section>

        {/* HISTORIQUE */}

        <section className="parent-card-v0">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}
          >
            <CalendarDays size={28} />

            <div>
              <h2 style={{ margin: 0 }}>
                Mes travaux
              </h2>

              <p
                className="muted"
                style={{
                  margin: '4px 0 0'
                }}
              >
                {homework.length}{' '}
                travail
                {homework.length > 1
                  ? 's'
                  : ''}
              </p>
            </div>
          </div>

          {loadingHomework && (
            <p className="muted">
              Chargement des travaux…
            </p>
          )}

          {!loadingHomework &&
            homework.length === 0 && (
              <p className="muted">
                Aucun travail soumis pour le moment.
              </p>
            )}

          {!loadingHomework &&
            homework.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gap: '14px'
                }}
              >
                {homework.map((item) => {
                  const status =
                    homeworkStatus(
                      item.status
                    );

                  const StatusIcon =
                    status.icon;

                  const editable =
                    item.status !==
                    'approved';

                  return (
                    <article
                      key={item.id}
                      style={{
                        padding: '18px',
                        border:
                          '1px solid #e5e7eb',
                        borderRadius: '14px'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent:
                            'space-between',
                          gap: '16px',
                          flexWrap: 'wrap'
                        }}
                      >
                        <div
                          style={{
                            flex: 1
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              flexWrap: 'wrap'
                            }}
                          >
                            <span
                              style={{
                                display:
                                  'inline-block',
                                padding:
                                  '5px 10px',
                                borderRadius:
                                  '999px',
                                background:
                                  '#eef4ff',
                                fontSize:
                                  '13px',
                                fontWeight: 700
                              }}
                            >
                              {item.className ||
                                item.classId}
                              {' • '}
                              {item.subjectName ||
                                item.subject ||
                                item.subjectId}
                            </span>

                            <span
                              style={{
                                display:
                                  'inline-flex',
                                alignItems:
                                  'center',
                                gap: '5px',
                                padding:
                                  '5px 10px',
                                borderRadius:
                                  '999px',
                                background:
                                  status.background,
                                fontSize:
                                  '13px',
                                fontWeight: 700
                              }}
                            >
                              <StatusIcon
                                size={14}
                              />

                              {status.label}
                            </span>
                          </div>

                          <h3
                            style={{
                              margin:
                                '12px 0 6px'
                            }}
                          >
                            {item.title ||
                              'Travail à faire'}
                          </h3>

                          <p
                            className="muted"
                            style={{
                              whiteSpace:
                                'pre-wrap'
                            }}
                          >
                            {item.instructions ||
                              item.description ||
                              'Aucune consigne.'}
                          </p>

                          <p>
                            <strong>
                              À rendre :
                            </strong>{' '}
                            {formatDeadline(
                              item.deadline
                            )}
                          </p>

                          {item.status ===
                            'pending' && (
                            <p
                              className="muted"
                            >
                              En attente de
                              validation par
                              l’administration.
                            </p>
                          )}

                          {item.status ===
                            'approved' && (
                            <p>
                              <strong>
                                Validé par
                                l’administration.
                              </strong>{' '}
                              Le travail est
                              maintenant visible
                              par les parents.
                            </p>
                          )}

                          {item.status ===
                            'rejected' && (
                            <div
                              style={{
                                marginTop:
                                  '12px',
                                padding:
                                  '12px',
                                borderRadius:
                                  '10px',
                                background:
                                  '#fee2e2'
                              }}
                            >
                              <strong>
                                Travail refusé
                              </strong>

                              <div
                                style={{
                                  marginTop:
                                    '5px'
                                }}
                              >
                                Motif :{' '}
                                {item.rejectionReason ||
                                  'Aucun motif précisé.'}
                              </div>
                            </div>
                          )}
                        </div>

                        {editable && (
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              alignItems:
                                'flex-start',
                              flexWrap: 'wrap'
                            }}
                          >
                            <button
                              type="button"
                              className="button button-secondary"
                              onClick={() =>
                                editHomework(
                                  item
                                )
                              }
                            >
                              <Edit3
                                size={16}
                              />

                              Modifier
                            </button>

                            <button
                              type="button"
                              className="button button-secondary"
                              disabled={
                                deletingId ===
                                item.id
                              }
                              onClick={() =>
                                removeHomework(
                                  item
                                )
                              }
                            >
                              <Trash2
                                size={16}
                              />

                              {deletingId ===
                              item.id
                                ? 'Suppression…'
                                : 'Supprimer'}
                            </button>
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
        </section>
    </TeacherShell>
  );
}
